import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, getRouteApi, Link } from "@tanstack/react-router";
import { Clock, ShieldAlert } from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";

import { CoachCalendar } from "~/components/engagement/dashboard/coach-calendar";
import { CoachTagCombobox } from "~/components/tags/coach-tag-combobox";
import { TaskListCard } from "~/components/tasks/task-list-card";
import { MarketplaceApprovalForm } from "~/components/ui/approval-form-modal";
import { GettingStartedChecklist } from "~/components/ui/getting-started-checklist";
import { PageHeader } from "~/components/ui/page-title";
import { WhatsNewHereLink } from "~/components/whats-new/whats-new-here-link";
import { useCoachPrivateProfileRequired } from "~/contexts/coach";
import { cn } from "~/lib/cn";
import { coachProgressQueryOptions } from "~/lib/queries/coachProgressQuery";
import { allEngagementsByCoachQueryOptions } from "~/lib/queries/engagementQuery";
import { programTemplatesQueryOptions } from "~/lib/queries/programTemplateQuery";
import type { MarketplaceStatus } from "~/lib/shared/coach-progress-types";
import { getHourInClientTimezone } from "~/lib/utils/timezone";
import { ClientOverview } from "~/routes/_user/my-coaching/-components/client-overview";
import { FirstClientPanel } from "~/routes/_user/my-coaching/-components/first-client-panel";

const parentRoute = getRouteApi("/_user/my-coaching");

const firstProgramQueryOptions = allEngagementsByCoachQueryOptions(1, 1);

/** Approved and pre-approved coaches are visible on the marketplace. */
function isLiveOnMarketplace(status: MarketplaceStatus): boolean {
  return status === "approved" || status === "pre_approved";
}

export const Route = createFileRoute("/_user/my-coaching/clients/dashboard")({
  staticData: { title: "Dashboard" },
  // Loads only what the view about to render reads, so it renders with its
  // data instead of a blank body or the wrong layout. Pending and revoked
  // coaches see static cards and fetch nothing.
  loader: async ({ context }) => {
    if (context.isRevoked || context.accountStatus === "pending") return;
    const { queryClient } = context;

    // fetchQuery, not ensureQueryData: right after a coach creates her first
    // program the cached count is stale at 0, and returning it would flash
    // the first-run panel before the cards. The count covers every status, so
    // a coach whose programs are all archived or finished keeps her cards.
    const [, programs] = await Promise.all([
      queryClient.ensureQueryData(coachProgressQueryOptions()),
      queryClient.fetchQuery(firstProgramQueryOptions),
    ]);
    if (programs.pagination.total > 0) return;

    // Only the first-run panel reads templates, and only its optional step;
    // prefetchQuery never throws, and the panel shows the step's own state.
    await queryClient.prefetchQuery(programTemplatesQueryOptions());
  },
  component: CoachDashboard,
});

function CoachDashboard() {
  const { accountStatus, isRevoked } = parentRoute.useLoaderData();
  const coach = useCoachPrivateProfileRequired();
  const coachName = coach?.firstName || "Coach";

  // Explicitly revoked — never render as "under review" (R24).
  if (isRevoked) {
    return <Tier1RevokedCard coachName={coachName} />;
  }

  // Tier 1: not yet approved as a coach
  if (accountStatus === "pending") {
    return <Tier1PendingCard coachName={coachName} />;
  }

  return <CoachApprovedView coachName={coachName} />;
}

/**
 * View for tier-1-approved coaches. It follows client work, not marketplace
 * approval: no program shows the steps to set up a first client, a program
 * shows tasks, calendar and clients. The profile checklist sits below until
 * the coach is live on the marketplace.
 */
function CoachApprovedView({ coachName }: { coachName: string }) {
  const coach = useCoachPrivateProfileRequired();
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const { data: progress } = useSuspenseQuery(coachProgressQueryOptions());
  const { data: programs } = useSuspenseQuery(firstProgramQueryOptions);

  const hasProgram = programs.pagination.total > 0;

  return (
    // The steps are one column of reading; the working cards use the width.
    <div className={cn("space-y-8", !hasProgram && "max-w-4xl")}>
      <PageHeader
        actions={<WhatsNewHereLink module="coaching_os" />}
        title={`${getGreeting()}, ${coachName}`}
      />

      {!hasProgram ? (
        <>
          <FirstClientPanel
            agreementSigned={!!coach.contractSigned}
            stripeReady={!!coach.stripeChargesEnabled}
          />
          <WelcomeLink />
        </>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <TaskListCard surface="coach" TagPicker={CoachTagCombobox} />
          <CoachCalendar />
          <ClientOverview />
        </div>
      )}

      {!isLiveOnMarketplace(progress.marketplaceStatus) && (
        <>
          <GettingStartedChecklist
            gettingStarted={progress.gettingStarted}
            marketplaceStatus={progress.marketplaceStatus}
            onRequestApproval={() => setApprovalModalOpen(true)}
          />
          <MarketplaceApprovalForm
            open={approvalModalOpen}
            onClose={() => setApprovalModalOpen(false)}
          />
        </>
      )}
    </div>
  );
}

/** Points to the six-figure path and NOVA instead of repeating them here. */
function WelcomeLink() {
  return (
    <div className="flex flex-col gap-2 rounded-2xl border border-purple-200 bg-purple-50 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <p className="text-text-secondary text-sm">
        Looking for the bigger picture? Welcome has your path to six figures and
        NOVA.
      </p>
      <Link
        to="/my-coaching/welcome"
        className="shrink-0 text-sm font-medium text-purple-700 hover:underline"
      >
        Open Welcome &rarr;
      </Link>
    </div>
  );
}

function getGreeting(): string {
  const hour = getHourInClientTimezone();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function Tier1PendingCard({ coachName }: { coachName: string }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
        className="w-full max-w-lg rounded-2xl border border-gray-100 bg-white p-6 shadow-sm"
      >
        <motion.div
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          className="mb-6 flex justify-center"
        >
          <div className="rounded-xl bg-amber-100 p-3">
            <Clock className="h-7 w-7 text-amber-600" />
          </div>
        </motion.div>
        <h2 className="mb-3 text-center font-heading text-xl font-medium text-gray-900">
          Application under review
        </h2>
        <p className="text-text-secondary text-center text-sm leading-relaxed">
          Thanks for submitting your coaching application, {coachName}. Our team
          is reviewing your profile and we&apos;ll notify you as soon as a
          decision has been made. This usually takes a few business days.
        </p>
      </motion.div>
    </div>
  );
}

function Tier1RevokedCard({ coachName }: { coachName: string }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
        className="w-full max-w-lg rounded-2xl border border-gray-100 bg-white p-6 shadow-sm"
      >
        <div className="mb-6 flex justify-center">
          <div className="rounded-xl bg-red-100 p-3">
            <ShieldAlert className="h-7 w-7 text-red-600" />
          </div>
        </div>
        <h2 className="mb-3 text-center font-heading text-xl font-medium text-gray-900">
          Your coach access has been revoked
        </h2>
        <p className="text-text-secondary text-center text-sm leading-relaxed">
          {coachName}, your coach access on Dream Coach Match has been revoked.
          If you believe this is a mistake, please reach out to{" "}
          <a
            href="mailto:support@dreamcoachmatch.com"
            className="font-medium text-gray-900 underline underline-offset-2"
          >
            support@dreamcoachmatch.com
          </a>{" "}
          for help.
        </p>
      </motion.div>
    </div>
  );
}
